import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Loader2, Zap, Clock } from 'lucide-react';
import { playSuccessSound, warmUpAudio } from '@/lib/sound';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FixedQRPay() {
  const { storeId, userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const prevStatusRef = useRef(null);
  const prevTxnRef = useRef(null);

  const fetchPending = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/pay/pending/${storeId}/${userId}`);
      if (!res.ok) throw new Error('Store not found');
      const d = await res.json();
      setData(d);
      setError(null);

      // If new transaction appeared, warm up audio
      if (d.has_pending && d.transaction_id !== prevTxnRef.current) {
        prevTxnRef.current = d.transaction_id;
        prevStatusRef.current = d.payment_status;
        warmUpAudio();
      }
    } catch {
      setError('Store not found');
    } finally {
      setLoading(false);
    }
  }, [storeId, userId]);

  // Initial fetch + polling every 3 seconds
  useEffect(() => {
    fetchPending();
    pollRef.current = setInterval(fetchPending, 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchPending]);

  // Auto-redirect to Stripe Checkout when pending payment found
  useEffect(() => {
    if (data?.has_pending && data?.checkout_url && data?.payment_status !== 'paid') {
      const timer = setTimeout(() => {
        window.location.href = data.checkout_url;
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [data?.has_pending, data?.checkout_url, data?.payment_status]);

  // WebSocket for real-time status when there's a pending payment
  useEffect(() => {
    if (!data?.has_pending || !data?.transaction_id || data?.payment_status === 'paid') return;

    const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = API_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/payment/${data.transaction_id}`;

    let ws;
    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'status') {
            if (msg.payment_status === 'paid' && prevStatusRef.current !== 'paid') {
              playSuccessSound();
            }
            prevStatusRef.current = msg.payment_status;
            setData(prev => prev ? { ...prev, payment_status: msg.payment_status } : prev);
          }
        } catch {}
      };
      ws.onclose = () => { setTimeout(connect, 3000); };
    };
    connect();
    return () => { if (ws) ws.close(); };
  }, [data?.transaction_id, data?.has_pending, data?.payment_status]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center">
          <Zap className="w-10 h-10 text-orange-500 mx-auto mb-3" />
          <p className="text-lg font-semibold text-slate-800">Page unavailable</p>
          <p className="text-sm text-slate-500 mt-1">This QR code is not valid.</p>
        </div>
      </div>
    );
  }

  const brandColor = data?.brand_color || '#F97316';
  const logoUrl = data?.logo_url;
  const fullLogoUrl = logoUrl ? (logoUrl.startsWith('http') ? logoUrl : `${API_URL}${logoUrl}`) : null;

  // Payment completed
  if (data?.has_pending && data?.payment_status === 'paid') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-sm w-full">
          <CheckCircle className="w-24 h-24 mx-auto mb-4" style={{ color: '#10B981' }} />
          <h1 className="text-3xl font-extrabold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Payment Received
          </h1>
          <p className="text-4xl font-extrabold mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {data.amount.toFixed(2)} {data.currency.toUpperCase()}
          </p>
          <p className="text-sm text-slate-500">Thank you for your payment at {data.store_name}!</p>
        </div>
      </div>
    );
  }

  // No pending payment — waiting screen
  if (!data?.has_pending) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: brandColor + '08' }}>
        <div className="text-center max-w-sm w-full">
          {fullLogoUrl && (
            <img src={fullLogoUrl} alt={data?.store_name} className="h-12 mx-auto mb-4 object-contain" />
          )}
          <h1 className="text-2xl font-extrabold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: brandColor }}>
            {data?.store_name}
          </h1>
          <div className="bg-white border border-slate-200 p-8 mt-4" data-testid="waiting-for-cashier">
            <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-semibold text-slate-700 mb-1">Waiting for cashier</p>
            <p className="text-sm text-slate-400">The payment amount will appear here automatically.</p>
            <div className="mt-4 flex items-center justify-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-xs">Listening...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Pending payment — show amount + redirecting message
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: brandColor + '08' }}>
      <div className="text-center max-w-sm w-full">
        {fullLogoUrl && (
          <img src={fullLogoUrl} alt={data.store_name} className="h-12 mx-auto mb-4 object-contain" />
        )}
        <h1 className="text-xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif', color: brandColor }}>
          {data.store_name}
        </h1>

        <div className="bg-white border-2 p-6 my-4" style={{ borderColor: brandColor }}>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">Amount to pay</p>
          <p className="text-5xl font-extrabold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {data.amount.toFixed(2)}
          </p>
          <p className="text-lg font-bold text-slate-400">{data.currency.toUpperCase()}</p>
        </div>

        <div className="flex items-center justify-center gap-2 text-slate-500 mb-4">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm font-semibold">Redirecting to payment...</span>
        </div>

        <button
          onClick={() => { window.location.href = data.checkout_url; }}
          className="w-full py-4 text-white font-bold text-base tracking-wide"
          style={{ backgroundColor: brandColor }}
          data-testid="pay-now-btn"
        >
          Pay {data.amount.toFixed(2)} {data.currency.toUpperCase()}
        </button>
      </div>
    </div>
  );
}
